<template>
  <div class="awards-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <b-table
        v-if="programs.length > 0"
        :items="enrichedPrograms"
        :fields="AWARDS_TABLE_FIELDS"
        hover
        responsive
        borderless
        show-empty
        class="awards-table__table"
      >
        <template #cell(is_active)="{ item }">
          <div class="awards-table__cell">
            <b-form-checkbox :checked="item.is_active" switch size="lg" class="awards-table__toggle" disabled />
          </div>
        </template>

        <template #cell(program_name)="{ item }">
          <div class="awards-table__cell awards-table__cell--name">
            <div class="awards-table__badge-wrapper">
              <div class="awards-table__badge-icon">
                <img
                  v-if="item.badge_presigned_url"
                  :src="item.badge_presigned_url"
                  :alt="item.program_name"
                  class="awards-table__badge-image"
                />
                <font-awesome-icon v-else :icon="['fad', 'award']" class="awards-table__badge-placeholder" />
              </div>
              <span :id="`type-tooltip-${item.recognition_award_program_id}`" class="awards-table__type-icon">
                <font-awesome-icon :icon="['fad', getProgramTypeIcon(item.program_type)]" />
              </span>
              <b-tooltip :target="`type-tooltip-${item.recognition_award_program_id}`" triggers="hover" placement="top">
                {{ getProgramTypeLabel(item.program_type) }}
              </b-tooltip>
            </div>
            <span class="awards-table__name">{{ item.program_name }}</span>
          </div>
        </template>

        <template #cell(scope)="{ item }">
          <div class="awards-table__cell">
            <template v-if="!item.enabled_divisions || item.enabled_divisions.length === 0">
              <b-badge pill variant="light" class="awards-table__scope-badge">entire company</b-badge>
            </template>
            <template v-else-if="item.division_names && item.division_names.length === 1">
              <b-badge pill variant="light" class="awards-table__scope-badge">{{ item.division_names[0] }}</b-badge>
            </template>
            <template v-else>
              <span :id="`scope-popover-${item.recognition_award_program_id}`" class="awards-table__scope-trigger">
                <b-badge pill variant="light" class="awards-table__scope-badge">
                  {{ item.enabled_divisions.length }}
                  {{ item.enabled_divisions.length === 1 ? 'division' : 'divisions' }}
                </b-badge>
              </span>
              <b-popover
                v-if="item.division_names && item.division_names.length > 1"
                :target="`scope-popover-${item.recognition_award_program_id}`"
                triggers="hover focus"
                placement="bottom"
              >
                <div class="awards-table__popover-container">
                  <div v-for="name in item.division_names" :key="name">
                    {{ name }}
                  </div>
                </div>
              </b-popover>
            </template>
          </div>
        </template>

        <template #cell(nominators)="{ item }">
          <div class="awards-table__cell">
            <template v-if="item.nominator_names && item.nominator_names.length === 1">
              <b-badge pill variant="light" class="awards-table__count-badge">{{ item.nominator_names[0] }}</b-badge>
            </template>
            <template v-else-if="item.nominator_names && item.nominator_names.length > 1">
              <span :id="`nominators-popover-${item.recognition_award_program_id}`" class="awards-table__scope-trigger">
                <b-badge pill variant="light" class="awards-table__count-badge">
                  {{ item.nominator_names.length }} nominators
                </b-badge>
              </span>
              <b-popover
                :target="`nominators-popover-${item.recognition_award_program_id}`"
                triggers="hover focus"
                placement="bottom"
              >
                <div class="awards-table__popover-container">
                  <div v-for="name in item.nominator_names" :key="name">
                    {{ name }}
                  </div>
                </div>
              </b-popover>
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
        </template>

        <template #cell(approvers)="{ item }">
          <div class="awards-table__cell">
            <template v-if="item.approver_names && item.approver_names.length === 1">
              <b-badge pill variant="light" class="awards-table__count-badge">{{ item.approver_names[0] }}</b-badge>
            </template>
            <template v-else-if="item.approver_names && item.approver_names.length > 1">
              <span :id="`approvers-popover-${item.recognition_award_program_id}`" class="awards-table__scope-trigger">
                <b-badge pill variant="light" class="awards-table__count-badge">
                  {{ item.approver_names.length }} approvers
                </b-badge>
              </span>
              <b-popover
                :target="`approvers-popover-${item.recognition_award_program_id}`"
                triggers="hover focus"
                placement="bottom"
              >
                <div class="awards-table__popover-container">
                  <div v-for="name in item.approver_names" :key="name">
                    {{ name }}
                  </div>
                </div>
              </b-popover>
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
        </template>

        <template #cell(budget)="{ item }">
          <div class="awards-table__cell awards-table__cell--budget">
            <template v-if="item.budget_minor">
              <div class="awards-table__budget-text">
                {{ formatBudget(item.budget_spent_minor ?? 0) }} / {{ formatBudget(item.budget_minor) }}
              </div>
              <b-progress
                :value="item.budget_spent_minor || 0"
                :max="item.budget_minor"
                height="4px"
                class="awards-table__budget-progress"
              />
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
        </template>

        <template #cell(budget_period)="{ item }">
          <div class="awards-table__cell">
            <template v-if="item.budgetPeriod">
              <span class="text-muted">({{ formatBudgetPeriod(item.budgetPeriod) }})</span>
            </template>
            <template v-else>
              <span class="text-muted"></span>
            </template>
          </div>
        </template>

        <template #cell(pending)="{ item }">
          <div class="awards-table__cell">
            <template v-if="item.pendingCount > 0">
              <span class="awards-table__pending-count">{{ item.pendingCount }}</span>
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div class="awards-table__cell awards-table__cell--actions">
            <router-link
              :to="{ name: 'recognition-award-detail', params: { programId: item.recognition_award_program_id } }"
              class="awards-table__action-arrow"
            >
              <font-awesome-icon :icon="['fal', 'chevron-right']" />
            </router-link>
          </div>
        </template>
      </b-table>

      <div v-else class="awards-table__empty">
        <p class="text-muted">No awards found.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import AppLoading from '@/components/AppLoading.vue';
import { toCurrency } from '@/utils';
import State from '@/state/State';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pendingNominations: {
    type: Array,
    default: () => [],
  },
});

const AWARDS_TABLE_FIELDS = [
  {
    key: 'is_active',
    label: 'Active',
    thStyle: { width: '80px' },
  },
  {
    key: 'program_name',
    label: 'Award name',
  },
  {
    key: 'scope',
    label: 'Scope',
  },
  {
    key: 'nominators',
    label: 'Nominators',
  },
  {
    key: 'approvers',
    label: 'Approvers',
  },
  {
    key: 'budget',
    label: 'Budget',
  },
  {
    key: 'budget_period',
    label: '',
    thStyle: { width: '100px' },
  },
  {
    key: 'pending',
    label: 'Pending',
  },
  {
    key: 'actions',
    label: '',
    thStyle: { width: '50px' },
  },
];

const currency = computed(() => State.state?.company?.default_currency);

// Count pending nominations per program
const pendingCountByProgramId = computed(() => {
  const map = {};
  for (const nomination of props.pendingNominations) {
    const id = nomination.recognition_award_program_id;
    if (id) {
      map[id] = (map[id] || 0) + 1;
    }
  }
  return map;
});

// Enrich programs with computed properties from existing data
const enrichedPrograms = computed(() => {
  return props.programs.map(program => {
    const properties = program.properties || {};
    const budgetPeriod = properties.budget_period;

    return {
      ...program,
      budgetPeriod,
      pendingCount: pendingCountByProgramId.value[program.recognition_award_program_id] || 0,
    };
  });
});

const formatBudget = value => {
  if (value == null) {
    return '-';
  }
  return toCurrency(value, currency.value).replace(/\.00$/, '');
};

const formatBudgetPeriod = period => {
  const periodMap = {
    week: 'weekly',
    month: 'monthly',
    quarter: 'quarterly',
    year: 'yearly',
  };
  return periodMap[period?.toLowerCase()] || period;
};

const getProgramTypeIcon = programType => {
  const iconMap = {
    PROGRAM: 'gift',
    OPEN: 'party-horn',
    MILESTONE: 'medal',
  };
  return iconMap[programType] || 'award';
};

const getProgramTypeLabel = programType => {
  const labelMap = {
    PROGRAM: 'Program Award',
    OPEN: 'Open Award',
    MILESTONE: 'Milestone Award',
  };
  return labelMap[programType] || programType;
};
</script>

<style scoped lang="scss">
.awards-table {
  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__cell {
    display: flex;
    align-items: center;

    &--name {
      gap: 0.75rem;
    }

    &--budget {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    &--actions {
      justify-content: flex-end;
    }
  }

  &__toggle {
    :deep(.custom-control-label::before),
    :deep(.custom-control-label::after) {
      width: 2.5rem;
      height: 1.25rem;
    }

    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
      background-color: var(--palette-color-brand-primary);
      border-color: var(--palette-color-brand-primary);
    }
  }

  &__badge-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__badge-image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  &__badge-placeholder {
    color: var(--palette-color-brand-primary);
    font-size: 1.25rem;
  }

  &__badge-wrapper {
    position: relative;
    flex-shrink: 0;
  }

  &__type-icon {
    position: absolute;
    bottom: -0.25rem;
    right: -0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    color: var(--palette-color-brand-primary);
    font-size: 0.625rem;
    cursor: default;
  }

  &__name {
    font-weight: 500;
  }

  &__scope-badge,
  &__count-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 400;
    padding: 0.375rem 0.75rem;
    cursor: default;
  }

  &__scope-trigger {
    cursor: pointer;
  }

  &__popover-container {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__budget-text {
    font-weight: 500;
  }

  &__pending-count {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--palette-color-brand-primary);
  }

  &__budget-progress {
    width: 100%;
    min-width: 80px;
    border-radius: 0.125rem;
    background-color: var(--palette-color-core-1-lighten-60);
  }

  &__action-arrow {
    color: var(--palette-color-default-lighten-40);
    padding: 0.5rem;

    &:hover {
      color: var(--palette-color-brand-primary);
    }
  }

  &__empty {
    padding: 2rem;
    text-align: center;
  }
}
</style>
